<template>
  <v-dialog v-model="localDialog" persistent max-width="900px">
    <v-card>
      <ModelTitle title="Material Templates" @close="close" />

      <v-card-text>
        <v-row>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              outlined
              dense
              label="Search templates"
              clearable
              @input="getList"
            />
          </v-col>

          <v-spacer />

          <v-col cols="12" md="3" class="text-right">
            <v-btn color="primary" @click="openCreate">
              Create Template
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          item-key="id"
          class="mt-2"
          dense
        >
          <template v-slot:item.questionCount="{ item }">
            {{ (item.fields && item.fields.length) ? item.fields.length : 0 }}
          </template>

          <template v-slot:item.action="{ item }">
            <v-btn icon @click="openEdit(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon @click="remove(item)">
              <v-icon color="red">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <v-divider class="my-4" />

        <div v-if="formOpen">
          <div class="subtitle-1 font-weight-medium mb-2">
            {{ editingId ? "Edit Template" : "Create Template" }}
          </div>

          <v-text-field
            v-model="form.name"
            outlined
            dense
            label="Template Name"
          />

          <v-textarea
            v-model="form.description"
            outlined
            dense
            rows="2"
            label="Description (optional)"
          />

          <div class="mt-3 font lead">
            Questions
          </div>

          <v-card outlined class="pa-2 mt-2">
            <div
              v-for="(q, idx) in form.fields"
              :key="q.key || idx"
              class="mb-2"
            >
              <v-row dense>
                <v-col cols="12" md="5">
                  <v-text-field
                    v-model="q.label"
                    outlined
                    dense
                    label="Question"
                    @blur="ensureKey(q)"
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-select
                    v-model="q.type"
                    :items="fieldTypes"
                    outlined
                    dense
                    label="Type"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-switch
                    v-model="q.required"
                    inset
                    label="Required"
                  />
                </v-col>

                <v-col cols="12" md="2" class="text-right">
                  <v-btn icon @click="removeField(idx)">
                    <v-icon color="red">mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <v-btn text color="primary" @click="addField">
              + Add Question
            </v-btn>
          </v-card>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn text color="secondary" @click="close">
          Close
        </v-btn>

        <v-btn
          v-if="formOpen"
          color="green"
          dark
          :loading="saving"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import termMaterialsService from "@/modules/TermMaterials/service";

export default {
  name: "TermMaterialTemplates",
  props: {
    dialog: { default: false },
  },
  data() {
    return {
      localDialog: false,
      search: "",
      loading: false,
      saving: false,
      items: [],
      headers: [
        { text: "Name", value: "name" },
        { text: "Questions", value: "questionCount" },
        { text: "Active", value: "isActive" },
        { text: "", value: "action", sortable: false, width: 120 },
      ],
      fieldTypes: ["text", "textarea"],
      formOpen: false,
      editingId: null,
      form: {
        name: "",
        description: "",
        fields: [],
        isActive: true,
      },
    };
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
      if (val) this.getList();
    },
  },
  methods: {
    close() {
      this.resetForm();
      this.$emit("close");
    },

    resetForm() {
      this.formOpen = false;
      this.editingId = null;
      this.form = { name: "", description: "", fields: [], isActive: true };
    },

    async getList() {
      this.loading = true;
      try {
        const res = await termMaterialsService.getTemplates({
          search: this.search || undefined,
        });
        
        this.items = (res && res.data && res.data) ? res.data : [];
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      this.resetForm();
      this.formOpen = true;
      this.addField();
    },

    openEdit(item) {
      this.formOpen = true;
      this.editingId = item.id;
      this.form = {
        name: item.name || "",
        description: item.description || "",
        fields: Array.isArray(item.fields) ? item.fields : [],
        isActive: item.isActive !== false,
      };
      if (!this.form.fields.length) this.addField();
    },

    addField() {
      this.form.fields.push({
        key: "",
        label: "",
        type: "text",
        required: true,
      });
    },

    removeField(idx) {
      this.form.fields.splice(idx, 1);
    },

    ensureKey(q) {
      if (!q) return;
      if (q.key) return;
      const label = (q.label || "").trim().toLowerCase();
      const safe = label.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
      q.key = safe || `q_${Date.now()}`;
    },

    validate() {
      if (!this.form.name || !this.form.name.trim()) return "Template name is required.";
      if (!Array.isArray(this.form.fields) || !this.form.fields.length) return "Add at least 1 question.";
      for (let i = 0; i < this.form.fields.length; i++) {
        const q = this.form.fields[i];
        if (!q.label || !q.label.trim()) return `Question ${i + 1} is empty.`;
        this.ensureKey(q);
      }
      return null;
    },

    async save() {
      const err = this.validate();
      if (err) {
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "error", title: err });
        return;
      }

      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description,
          fields: this.form.fields,
          isActive: this.form.isActive,
        };

        if (this.editingId) {
          await termMaterialsService.updateTemplate(this.editingId, payload);
        } else {
          await termMaterialsService.createTemplate(payload);
        }

        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "success", title: "Template saved" });

        await this.getList();
        this.resetForm();
      } catch (e) {
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "error", title: (e && e.data && e.data.message) ? e.data.message : "Save failed" });
      } finally {
        this.saving = false;
      }
    },

    remove(item) {
      this.$swal({
        title: "Delete template?",
        text: "This will remove the template.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
      }).then(async (r) => {
        if (!r.value) return;
        await termMaterialsService.deleteTemplate(item.id);
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "success", title: "Deleted" });
        this.getList();
      });
    },
  },
};
</script>